<template>
  <div class="edit-profile-page">
    <!-- Custom Header -->
    <header class="ep-header">
      <button class="ep-header__back" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </button>
      <h1 class="ep-header__title">{{ $lang.editProfile_title || 'EDIT PROFILE' }}</h1>
      <button class="ep-header__save" :disabled="saving" @click="handleSave">
        {{ $lang.editProfile_save || 'SAVE' }}
      </button>
    </header>
    <div class="ep-header__spacer"></div>

    <div v-if="readError" class="ep-read-error">
      {{ readError }}
    </div>

    <div class="ep-content">
      <!-- Avatar Section -->
      <div class="ep-avatar" @click="$jumpTo('/avatar', { headUrl })">
        <div class="ep-avatar__wrapper">
          <img
            v-if="avatarSrc"
            :src="avatarSrc"
            class="ep-avatar__img"
          />
          <div v-else class="ep-avatar__placeholder"></div>
          <div class="ep-avatar__camera">
            <van-icon name="photograph" size="16" color="#1a1d29" />
          </div>
        </div>
      </div>

      <!-- User ID (readonly + copy) -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_user_id || 'User ID' }}</label>
        <div class="ep-field__input ep-field__input--readonly">
          <span>ID: {{ inviteCode }}</span>
          <button class="ep-field__copy" @click="copyId">
            <van-icon name="records" size="16" color="var(--vk-t2)" />
          </button>
        </div>
      </div>

      <!-- Phone -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_phone || 'Phone' }}</label>
        <div class="ep-field__input ep-field__input--readonly">
          <span>{{ phone || '--' }}</span>
        </div>
      </div>

      <!-- Nickname -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_nickname || 'Nickname' }}</label>
        <div class="ep-field__input">
          <input
            v-model.trim="nickName"
            class="ep-field__text"
            :placeholder="$lang.editProfile_nickname_ph || 'Enter nickname'"
            maxlength="32"
          />
        </div>
      </div>

      <!-- Area code -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_area_code || 'Area code' }}</label>
        <div class="ep-field__input">
          <input
            v-model.trim="areaCode"
            class="ep-field__text"
            :placeholder="$lang.editProfile_area_code_ph || 'Enter area code'"
            maxlength="12"
          />
        </div>
      </div>

      <!-- Gender -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_gender || 'Gender' }}</label>
        <div class="ep-field__input">
          <select v-model="gender" class="ep-field__select">
            <option value="">{{ $lang.common_please_select || 'Please select' }}</option>
            <option value="male">{{ $lang.editProfile_gender_male || 'Male' }}</option>
            <option value="female">{{ $lang.editProfile_gender_female || 'Female' }}</option>
            <option value="other">{{ $lang.editProfile_gender_other || 'Other' }}</option>
          </select>
        </div>
      </div>

      <!-- Birthday -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_birthday || 'Birthday' }}</label>
        <div
          class="ep-field__input"
          style="cursor: pointer"
          @click="showBirthdayPicker = true"
        >
          <span :style="{ color: birthday ? '#ffffff' : '#4b5563' }">
            {{ birthday || ($lang.editProfile_birthday_ph || 'Select birthday') }}
          </span>
          <van-icon name="calendar-o" size="16" color="var(--vk-t2)" />
        </div>
      </div>
    </div>

    <DatePickerPopup
      v-model="showBirthdayPicker"
      :min-date="minDate"
      :max-date="maxDate"
      :default-date="pickerDate"
      :confirm-text="$lang.editProfile_save || 'Confirm'"
      :cancel-text="$lang.common_cancel || 'Cancel'"
      @confirm="onBirthdayConfirm"
    />
  </div>
</template>

<script>
import { Init, ChangeExtend } from '@/api/common'
import DatePickerPopup from '@/components/DatePickerPopup'
import { contentOf, failMessage, handleNeedLogin, hasUnavailableMarker, isOk, isSafeWriteSuccess } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'
import { copyTextToClipboard } from '@/components/share/shareSafety'

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) { return null }
  return null
}

function isReadableProfileResponse(res) {
  return isOk(res) && !hasUnavailableMarker(res)
}

function profileFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

const MINE_BLOCKED_MARKER_KEYS = ['failClosed', 'fail_closed', 'falseProof', 'false_proof', 'readOnlyAction', 'writeBlocked', 'backendBlocked', 'backendFailure', 'backend_failure', 'technicalMarker', 'technical_marker', 'mock', 'fallback']
const MINE_FALSE_PROOF_KEYS = ['writeProof', 'write_proof', 'affectedRowsProof', 'affected_rows_proof']
const MINE_STRICT_WRITE_PROOF_KEYS = ['writeProof', 'write_proof', 'serverProof', 'server_proof', 'affectedProof', 'affected_proof', 'affectedRowsProof', 'affected_rows_proof']
const MINE_WRITE_COUNT_PROOF_KEYS = ['affectedRows', 'affected_rows', 'affected', 'rowCount', 'row_count']
const MINE_FALSEY_PROOF_TOKENS = ['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder', 'fallback', 'stub', 'blocked']
const MINE_BLOCKED_PROOF_TOKENS = ['backendfailure', 'backend_failure', 'technicalmarker', 'technical_marker']

function mineToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function hasMineBlockedWriteMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasMineBlockedWriteMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const token = String(value == null ? '' : value).trim().toLowerCase()
    if (MINE_BLOCKED_MARKER_KEYS.includes(key) && value !== undefined && value !== null && value !== '') return true
    if (MINE_FALSE_PROOF_KEYS.includes(key) && (value === false || value === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(token))) return true
    if (token === 'placeholder') return true
    return value && typeof value === 'object' && hasMineBlockedWriteMarker(value, depth + 1, seen)
  })
}

function hasPositiveMineWriteCount(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value !== 'string') return false
  const token = mineToken(value)
  return /^\d+$/.test(token) && !/^0+$/.test(token)
}

function hasStrictMineProofValue(value) {
  if (Array.isArray(value) || (value && typeof value === 'object')) return false
  if (value === true || value === false || value === 0) return false
  const token = mineToken(value)
  return !!token &&
    token !== 'true' &&
    !MINE_FALSEY_PROOF_TOKENS.includes(token) &&
    !token.includes('mock') &&
    !token.includes('placeholder') &&
    !token.includes('fallback') &&
    !token.includes('stub') &&
    !token.includes('read_only') &&
    !token.includes('read-only') &&
    !token.includes('read only') &&
    !token.includes('readonly') &&
    !token.includes('blocked') &&
    !MINE_BLOCKED_PROOF_TOKENS.some(marker => token.includes(marker)) &&
    !token.includes('unavailable') &&
    !token.includes('unimplemented') &&
    !token.includes('fail') &&
    !token.includes('error')
}

function hasStrictMineWriteProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasStrictMineWriteProof(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (MINE_WRITE_COUNT_PROOF_KEYS.includes(key) && hasPositiveMineWriteCount(value)) return true
    if (MINE_STRICT_WRITE_PROOF_KEYS.includes(key) && hasStrictMineProofValue(value)) return true
    return value && typeof value === 'object' && hasStrictMineWriteProof(value, depth + 1, seen)
  })
}

function isMineSafeWriteSuccess(res) {
  return isSafeWriteSuccess(res) && hasStrictMineWriteProof(res) && !hasMineBlockedWriteMarker(res)
}

export default {
  name: 'EditProfile',
  components: { DatePickerPopup },
  data() {
    return {
      headUrl: null,
      inviteCode: '',
      phone: '',
      nickName: '',
      areaCode: '',
      gender: '',
      birthday: '',
      showBirthdayPicker: false,
      pickerDate: new Date(),
      minDate: new Date(1950, 0, 1),
      maxDate: new Date(),
      saving: false,
      readError: ''
    }
  },
  computed: {
    avatarSrc() {
      const index = Number(this.headUrl)
      if (!Number.isInteger(index) || index < 0 || index > 7) return ''
      return require(`@/assets/img/avatar/avatar_${index}.jpg`)
    }
  },
  mounted() {
    this.loadProfile()
  },
  methods: {
    ensureLogin() {
      if (safeGetStorageItem(getLocalStorage(), 'token')) return true
      if (this.$bus) this.$bus.$emit('openLogin')
      this.$jumpTo('/home', {}, { replace: true })
      return false
    },
    async loadProfile() {
      if (!this.ensureLogin()) return
      try {
        const data = await Init()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          this.readError = failMessage(data, this.$lang.service_unavailable || 'Service temporarily unavailable. Please try again later.')
          return
        }
        if (isReadableProfileResponse(data)) {
          const content = contentOf(data)
          this.readError = ''
          this.headUrl = content.headUrl
          this.inviteCode = content.inviteCode || ''
          this.phone = content.phone || content.phoneNo || content.account || ''
          this.nickName = content.nickName || content.nick_name || content.account || ''
          this.areaCode = content.areaCode || content.area_code || ''
          this.gender = String(content.gender || content.sex || '')
          this.birthday = content.birthday || ''
          if (this.birthday) {
            const d = new Date(this.birthday)
            if (!isNaN(d.getTime())) this.pickerDate = d
          }
        } else {
          const message = failMessage(data, this.$lang.profile_read_failed || 'Profile data could not be loaded. Existing data has been kept.')
          this.readError = message
          this.$toast({ message, icon: 'cross' })
        }
      } catch (e) {
        const message = profileFailureMessage(e, this.$lang.profile_read_failed || 'Profile data could not be loaded. Existing data has been kept.')
        this.readError = message
        this.$toast({ message, icon: 'cross' })
      }
    },
    onBirthdayConfirm(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      this.birthday = `${y}-${m}-${d}`
      this.showBirthdayPicker = false
    },
    copyId() {
      const text = this.inviteCode
      if (!text) {
        if (typeof this.$toast === 'function') {
          this.$toast({ message: this.$lang.noempt || 'No data', icon: 'cross' })
        }
        return
      }
      let copied = false
      try {
        copied = copyTextToClipboard(text)
      } catch (e) {
        copied = false
      }
      if (typeof this.$toast === 'function') {
        this.$toast({
          message: copied ? (this.$lang.Sucesso || 'Success') : (this.$lang.copy_failed || 'Copy failed'),
          icon: copied ? 'success' : 'cross'
        })
      }
    },
    async handleSave() {
      if (this.saving) return
      if (!this.ensureLogin()) return
      if (this.readError) {
        this.$toast({ message: this.readError, icon: 'cross' })
        return
      }
      this.saving = true
      try {
        const res = await ChangeExtend({
          nickName: this.nickName,
          areaCode: this.areaCode,
          birthday: this.birthday || '',
          gender: this.gender
        })
        if (handleNeedLogin(this, res)) return
        if (isSafeWriteSuccess(res) && isMineSafeWriteSuccess(res)) {
          this.$toast({ message: this.$lang.Sucesso || 'Saved', icon: 'success' })
          this.$router.go(-1)
        } else {
          this.$toast({ message: failMessage(res, this.$lang.save_failed || 'Save failed. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        this.$toast({ message: profileFailureMessage(e, this.$lang.service_unavailable || 'Service temporarily unavailable. Please try again later.'), icon: 'cross' })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.edit-profile-page {
  background-color: #1a1d29;
  min-height: 100vh;
  color: #ffffff;
}

.ep-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: #1a1d29;
  z-index: 999;

  &__back {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
  }

  &__title {
    font-size: var(--vk-fs-lg);
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #ffffff;
    margin: 0;
  }

  &__save {
    background: none;
    border: none;
    color: #ff5a1f;
    font-size: var(--vk-fs-base);
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
    padding: 0;

    &:disabled {
      color: var(--vk-t3);
      cursor: default;
    }
  }

  &__spacer {
    height: 55px;
  }
}

.ep-content {
  padding-bottom: 24px;
}

.ep-read-error {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(220, 38, 38, 0.12);
  border: 1px solid rgba(220, 38, 38, 0.28);
  color: #f87171;
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}

.ep-avatar {
  display: flex;
  justify-content: center;
  margin: 24px auto;
  cursor: pointer;

  &__wrapper {
    position: relative;
    width: 128px;
    height: 128px;
    border-radius: 50%;
    border: 4px solid #2a2e3d;
    overflow: visible;
  }

  &__img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: #2a2e3d;
  }

  &__camera {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #ff5a1f;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #1a1d29;
  }
}

.ep-field {
  margin-bottom: 20px;
  padding: 0 16px;

  &__label {
    display: block;
    font-size: var(--vk-fs-xs);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--vk-t2);
    margin-bottom: 8px;
  }

  &__input {
    background-color: #252936;
    border: 1px solid #3f4451;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #ffffff;
    font-size: var(--vk-fs-base);
    transition: border-color 0.2s ease;

    &:focus-within {
      border-color: #ff5a1f;
      box-shadow: 0 0 0 2px rgba(255, 90, 31, 0.15);
    }

    &--readonly {
      opacity: 0.7;

      &:focus-within {
        border-color: #3f4451;
        box-shadow: none;
      }
    }
  }

  &__text {
    background: transparent;
    border: none;
    color: #ffffff;
    width: 100%;
    outline: none;
    font-size: var(--vk-fs-base);
    padding: 0;
    margin: 0;

    &::placeholder {
      color: #4b5563;
    }
  }

  &__select {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: #ffffff;
    font-size: var(--vk-fs-base);

    option {
      color: #111827;
    }
  }

  &__copy {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 8px;
  }
}
</style>
